import { useState, useEffect } from 'react';

const SITE_URL = 'https://hoosierlifeinsurance.com';

const faqs = [
  { q: 'How much life insurance does a family in Indianapolis need?', a: 'A common rule is 10–12x your annual income, plus your mortgage balance, plus future education costs. Our Family Coverage Calculator gives you a personalized number based on your specific situation.' },
  { q: 'What is the best life insurance for families in Indianapolis?', a: 'Term life insurance is the most popular choice for Indianapolis families — it provides the highest coverage at the lowest cost. A 20-year term policy covers your family through the years they need it most.' },
  { q: 'How much does family life insurance cost in Indianapolis?', a: 'A healthy 35-year-old in Indianapolis can get $500,000 of 20-year term life insurance for as little as $22–$28/month. Dual-income households often get two separate policies for complete protection.' },
  { q: 'Should both spouses have life insurance?', a: 'Yes. Both income earners should have coverage. Even a stay-at-home parent provides significant economic value — childcare, household management — that would need to be replaced. We recommend separate policies for each spouse.' },
  { q: 'What happens to my family\'s mortgage if I die without life insurance?', a: 'Without life insurance, your family may be forced to sell the home or struggle to make mortgage payments on a single income. A term life or mortgage protection policy ensures your family can stay in their home.' },
  { q: 'When is the best time for a family to buy life insurance?', a: 'The best time is now — rates increase with age and any change in health. A 30-year-old pays significantly less than a 40-year-old for the same coverage. New parents especially should prioritize getting covered immediately.' },
  { q: 'Can I get life insurance while pregnant?', a: 'Yes. Pregnancy is not a disqualifying condition for most life insurance policies. In fact, getting covered during pregnancy locks in your current health rating before any potential postpartum health changes.' },
  { q: 'What is mortgage protection insurance for Indianapolis families?', a: 'Mortgage protection insurance is a type of term life policy specifically designed to pay off your mortgage if you die. It ensures your family can stay in their home regardless of what happens to the primary income earner.' },
  { q: 'How do I get a family life insurance quote in Indianapolis?', a: 'Call us at (317) 648-2983 or use the Family Coverage Calculator on this page. As an independent agent, Benjamin Wright compares 20+ carriers to find the best rate for your family — free, no obligation.' },
  { q: 'Does Hoosier Life Insurance serve families outside Indianapolis?', a: 'Yes. We serve families throughout Central Indiana including Carmel, Fishers, Noblesville, Westfield, Zionsville, Greenwood, Avon, and all surrounding communities.' },
];

export default function LifeInsuranceForFamiliesIndianapolisPage() {
  const [mortgage, setMortgage] = useState(250000);
  const [income, setIncome] = useState(75000);
  const [dependents, setDependents] = useState(2);
  const [spouseIncome, setSpouseIncome] = useState(55000);
  const [yearsToRetirement, setYearsToRetirement] = useState(25);
  const [calcResult, setCalcResult] = useState<null | { total: number; termRec: string; monthlyEst: string; breakdown: { label: string; amount: number }[] }>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'Life Insurance for Families Indianapolis IN | Family Coverage Calculator | Hoosier Life';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Life insurance for families in Indianapolis, Indiana. Family Coverage Calculator factors in mortgage, income & dependents. Compare 20+ carriers. Free quotes for dual-income households, new parents & homeowners.');
    const canonical = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', `${SITE_URL}/life-insurance-for-families-indianapolis`);
    if (!document.querySelector('link[rel="canonical"]')) document.head.appendChild(canonical);

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/life-insurance-for-families-indianapolis#webpage`,
          url: `${SITE_URL}/life-insurance-for-families-indianapolis`,
          name: 'Life Insurance for Families Indianapolis IN | Family Coverage Calculator | Hoosier Life',
          description: 'Life insurance for families in Indianapolis, Indiana. Family Coverage Calculator factors in mortgage, income & dependents.',
          dateModified: '2026-04-15',
        },
        {
          '@type': 'FAQPage',
          mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        },
      ],
    };
    const existing = document.getElementById('families-indy-schema');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'families-indy-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, []);

  const calculateCoverage = () => {
    const incomeReplacement = income * Math.min(yearsToRetirement, 20);
    const mortgagePayoff = mortgage;
    const childcareAndEducation = dependents * 50000;
    const emergencyFund = income * 0.5;
    const total = incomeReplacement + mortgagePayoff + childcareAndEducation + emergencyFund;
    const rounded = Math.ceil(total / 50000) * 50000;
    const termRec = yearsToRetirement >= 25 ? '30-Year Term' : yearsToRetirement >= 15 ? '20-Year Term' : '15-Year Term';
    const monthlyBase = rounded / 1000000 * 22;
    const monthlyEst = `$${Math.round(monthlyBase * 10) / 10}–$${Math.round(monthlyBase * 1.4 * 10) / 10}/mo`;
    setCalcResult({
      total: rounded,
      termRec,
      monthlyEst,
      breakdown: [
        { label: 'Income Replacement', amount: incomeReplacement },
        { label: 'Mortgage Payoff', amount: mortgagePayoff },
        { label: 'Child Education & Care', amount: childcareAndEducation },
        { label: 'Emergency Fund', amount: emergencyFund },
      ],
    });
  };

  const fmt = (n: number) => '$' + n.toLocaleString();

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Hero */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/38242e7d044ffed23250f1c9326941d3.jpg"
            alt="Indianapolis family protected by life insurance"
            className="w-full h-full object-cover object-top"
            width={1440}
            height={700}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2A3F]/90 via-[#1A3A52]/80 to-[#0F2A3F]/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
          <nav className="flex items-center gap-2 text-sm text-white/60 mb-8">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Life Insurance for Families Indianapolis</span>
          </nav>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D97642]/25 text-[#F0A070] text-xs font-bold uppercase tracking-widest rounded-full border border-[#D97642]/40 mb-5">
              <i className="ri-home-heart-line"></i>
              Indianapolis Families · Dual Income · New Parents · Homeowners
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-[56px] font-extrabold text-white leading-[1.1] mb-6">
              Life Insurance for<br />
              <span className="text-[#F0A070]">Indianapolis Families</span>
            </h1>
            <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-2xl">
              Protect your mortgage, your income, and your children's future. Our <strong className="text-white">Family Coverage Calculator</strong> shows you exactly how much coverage your Indianapolis family needs — and what it costs from 20+ top-rated carriers.
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              {[
                { icon: 'ri-home-heart-line', label: 'Mortgage Protection' },
                { icon: 'ri-user-heart-line', label: 'Dual-Income Coverage' },
                { icon: 'ri-parent-line', label: 'New Parent Specialists' },
                { icon: 'ri-map-pin-line', label: 'Local Indianapolis Agent' },
              ].map(b => (
                <div key={b.label} className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/20 backdrop-blur-sm">
                  <i className={`${b.icon} text-[#D97642] text-sm`}></i>
                  <span className="text-sm text-white/90 font-medium">{b.label}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/book-appointment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white font-bold rounded-full hover:bg-[#C26638] transition-all whitespace-nowrap">
                <i className="ri-calendar-check-fill text-lg"></i>
                Get My Family Quote Free
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-bold rounded-full hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Family Coverage Calculator */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-[#D97642]/15 text-[#D97642] text-xs font-bold uppercase tracking-widest rounded-full mb-4">Interactive Tool</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A3A52] mb-4">Family Coverage Calculator</h2>
            <p className="text-gray-600 max-w-xl mx-auto">Enter your family's numbers — mortgage, income, and dependents — and we'll calculate exactly how much life insurance your Indianapolis family needs.</p>
          </div>

          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Mortgage */}
              <div>
                <label className="block text-sm font-bold text-[#1A3A52] mb-2">Mortgage Balance</label>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-lg font-bold text-[#D97642]">{fmt(mortgage)}</span>
                </div>
                <input type="range" min={0} max={800000} step={10000} value={mortgage} onChange={e => setMortgage(Number(e.target.value))}
                  className="w-full accent-[#D97642]" />
                <div className="flex justify-between text-xs text-gray-400 mt-1"><span>$0</span><span>$800K</span></div>
              </div>
              {/* Primary Income */}
              <div>
                <label className="block text-sm font-bold text-[#1A3A52] mb-2">Your Annual Income</label>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-lg font-bold text-[#D97642]">{fmt(income)}</span>
                </div>
                <input type="range" min={20000} max={300000} step={5000} value={income} onChange={e => setIncome(Number(e.target.value))}
                  className="w-full accent-[#D97642]" />
                <div className="flex justify-between text-xs text-gray-400 mt-1"><span>$20K</span><span>$300K</span></div>
              </div>
              {/* Spouse Income */}
              <div>
                <label className="block text-sm font-bold text-[#1A3A52] mb-2">Spouse / Partner Income</label>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-lg font-bold text-[#D97642]">{fmt(spouseIncome)}</span>
                </div>
                <input type="range" min={0} max={300000} step={5000} value={spouseIncome} onChange={e => setSpouseIncome(Number(e.target.value))}
                  className="w-full accent-[#D97642]" />
                <div className="flex justify-between text-xs text-gray-400 mt-1"><span>$0</span><span>$300K</span></div>
              </div>
              {/* Dependents */}
              <div>
                <label className="block text-sm font-bold text-[#1A3A52] mb-2">Number of Dependents</label>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-lg font-bold text-[#D97642]">{dependents} {dependents === 1 ? 'child' : 'children'}</span>
                </div>
                <input type="range" min={0} max={6} step={1} value={dependents} onChange={e => setDependents(Number(e.target.value))}
                  className="w-full accent-[#D97642]" />
                <div className="flex justify-between text-xs text-gray-400 mt-1"><span>0</span><span>6</span></div>
              </div>
              {/* Years to Retirement */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-[#1A3A52] mb-2">Years Until Retirement</label>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-lg font-bold text-[#D97642]">{yearsToRetirement} years</span>
                </div>
                <input type="range" min={5} max={35} step={1} value={yearsToRetirement} onChange={e => setYearsToRetirement(Number(e.target.value))}
                  className="w-full accent-[#D97642]" />
                <div className="flex justify-between text-xs text-gray-400 mt-1"><span>5 yrs</span><span>35 yrs</span></div>
              </div>
            </div>

            <button
              onClick={calculateCoverage}
              className="w-full py-4 bg-[#D97642] text-white font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap text-base"
            >
              <i className="ri-calculator-line mr-2"></i>
              Calculate My Family's Coverage Need
            </button>

            {calcResult && (
              <div className="mt-8 p-6 bg-[#F9F7F4] rounded-xl border border-gray-100">
                <div className="text-center mb-6">
                  <div className="text-sm text-gray-500 mb-1">Your Family Needs</div>
                  <div className="text-5xl font-extrabold text-[#D97642]">{fmt(calcResult.total)}</div>
                  <div className="text-sm text-gray-500 mt-1">in life insurance coverage</div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {calcResult.breakdown.map(b => (
                    <div key={b.label} className="bg-white rounded-lg p-3 border border-gray-100">
                      <div className="text-xs text-gray-500 mb-1">{b.label}</div>
                      <div className="text-base font-bold text-[#1A3A52]">{fmt(b.amount)}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-[#1A3A52] rounded-xl p-4 text-center">
                    <div className="text-xs text-white/60 mb-1">Recommended Policy</div>
                    <div className="text-lg font-extrabold text-white">{calcResult.termRec}</div>
                  </div>
                  <div className="bg-[#D97642] rounded-xl p-4 text-center">
                    <div className="text-xs text-white/80 mb-1">Estimated Monthly Cost</div>
                    <div className="text-lg font-extrabold text-white">{calcResult.monthlyEst}</div>
                  </div>
                </div>
                <a href="/book-appointment" className="block text-center py-3.5 bg-[#1A3A52] text-white font-bold rounded-xl hover:bg-[#0F2A3F] transition-all whitespace-nowrap">
                  <i className="ri-calendar-check-fill mr-2"></i>
                  Get My Exact Family Rate — Free
                </a>
                <p className="text-xs text-gray-400 text-center mt-3">Estimates only. Actual rates depend on age, health, and carrier. No obligation.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Who We Help */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A3A52] mb-4">Indianapolis Families We Help Every Day</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-parent-line',
                title: 'New Parents',
                desc: 'Just had a baby? This is the most important time to get covered. Lock in your lowest rate now — before any health changes — and protect your child\'s future from day one.',
                rate: 'From $18/mo',
                coverage: '$500K coverage',
              },
              {
                icon: 'ri-home-heart-line',
                title: 'Homeowners',
                desc: 'Your mortgage is likely your family\'s biggest financial obligation. A term life policy ensures your family can stay in their Indianapolis home no matter what happens.',
                rate: 'From $22/mo',
                coverage: '$500K coverage',
              },
              {
                icon: 'ri-briefcase-line',
                title: 'Dual-Income Households',
                desc: 'When both spouses work, both incomes need protection. We set up separate policies for each spouse — often for less than $50/month combined for a young, healthy couple.',
                rate: 'From $40/mo',
                coverage: '$1M combined',
              },
            ].map(item => (
              <div key={item.title} className="bg-[#F9F7F4] rounded-2xl p-7 border border-gray-100">
                <div className="w-14 h-14 flex items-center justify-center bg-[#D97642]/10 rounded-xl mb-5">
                  <i className={`${item.icon} text-[#D97642] text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A3A52] mb-3">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-5">{item.desc}</p>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                  <span className="text-sm font-bold text-[#D97642]">{item.rate}</span>
                  <span className="text-sm text-gray-500">{item.coverage}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-[#1A3A52] mb-4">Indianapolis Families We've Protected</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { name: 'The Martinez Family', location: 'Fishers, IN', quote: 'We had our second baby and realized we had zero coverage. Benjamin got us both covered — $500K each — for $44/month combined. We had no idea it was that affordable.', badge: '$44/mo · $1M combined' },
              { name: 'The Johnson Family', location: 'Carmel, IN', quote: 'We bought our first home and needed mortgage protection. Benjamin found us a 30-year term policy that covers our entire mortgage for $28/month. Total peace of mind.', badge: '$28/mo · $350K mortgage' },
              { name: 'The Williams Family', location: 'Noblesville, IN', quote: 'My husband and I both work. We needed coverage for both of us. Benjamin set up two separate policies and saved us $600/year compared to what our employer was offering.', badge: '$38/mo · Dual coverage' },
            ].map(t => (
              <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>)}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed italic mb-4">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-[#1A3A52]">{t.name}</div>
                    <div className="text-xs text-gray-400">{t.location}</div>
                  </div>
                  <span className="px-2.5 py-1 bg-[#D97642]/10 text-[#D97642] text-xs font-bold rounded-full">{t.badge}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A3A52] mb-4">Family Life Insurance FAQ — Indianapolis</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#F9F7F4] rounded-xl border border-gray-100 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer">
                  <span className="font-semibold text-[#1A3A52] pr-4 text-sm">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-[#D97642] text-xl flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1A3A52]">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">Protect Your Indianapolis Family Today</h2>
          <p className="text-white/75 mb-8 text-lg">Compare 20+ carriers. Get your family covered for as little as $18/month. Free, no obligation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/book-appointment" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#D97642] text-white font-bold rounded-full hover:bg-[#C26638] transition-all whitespace-nowrap">
              <i className="ri-calendar-check-fill text-lg"></i>
              Book My Free Family Consultation
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-bold rounded-full hover:bg-white/20 transition-all border border-white/30 whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              Call (317) 648-2983
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
